import { Link } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { AlertTriangle, Eye, Pencil, Plus, UserPlus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { GovernanceDataTable } from "@/components/governance/GovernanceDataTable";
import { Committee, CommitteeMember, TermsOfReference } from "@/types/governance";

interface FrameworkMasterTableProps {
  committees: Committee[];
  members: CommitteeMember[];
  terms: TermsOfReference[];
  onAssignOwner?: (committeeId: string) => void;
  onAddSubCommittee?: (committeeId: string) => void;
  onEditCommittee?: (committeeId: string) => void;
  onViewCommittee?: (committeeId: string) => void;
}

interface FrameworkTableRow {
  id: string;
  name: string;
  description: string;
  level: number;
  ownerName: string;
  chairName: string;
  memberProgress: string;
  parentName: string;
  torCommitteeId: string | null;
}

export function FrameworkMasterTable({
  committees,
  members,
  terms,
  onAssignOwner,
  onAddSubCommittee,
  onEditCommittee,
  onViewCommittee,
}: FrameworkMasterTableProps) {
  const levelBadgeClassMap: Record<number, string> = {
    1: "border-violet-300 bg-violet-100 text-violet-800",
    2: "border-blue-300 bg-blue-100 text-blue-800",
    3: "border-emerald-300 bg-emerald-100 text-emerald-800",
    4: "border-amber-300 bg-amber-100 text-amber-800",
    5: "border-rose-300 bg-rose-100 text-rose-800",
  };

  const memberById = new Map(members.map((item) => [item.id, item]));
  const committeeById = new Map(committees.map((item) => [item.id, item]));
  const torByCommitteeId = new Map(terms.map((item) => [item.committeeId, item]));

  const rows: FrameworkTableRow[] = committees.map((committee) => {
    const owner = committee.ownerId ? memberById.get(committee.ownerId) : undefined;
    const chair = committee.chairpersonId ? memberById.get(committee.chairpersonId) : undefined;
    const parent = committee.parentId ? committeeById.get(committee.parentId) : undefined;
    const tor = torByCommitteeId.get(committee.id);
    return {
      id: committee.id,
      name: committee.name,
      description: committee.description,
      level: committee.level,
      ownerName: owner?.name ?? "Owner Missing",
      chairName: chair?.name ?? "Chair Missing",
      memberProgress: `${committee.memberIds.length}/${committee.minimumMembersRequired}`,
      parentName: parent?.name || "-",
      torCommitteeId: tor ? committee.id : null,
    };
  });

  const columns: ColumnDef<FrameworkTableRow>[] = [
    {
      accessorKey: "name",
      header: "Committee Name",
      cell: ({ row }) => (
        <div>
          <button
            type="button"
            onClick={() => onViewCommittee?.(row.original.id)}
            className="font-medium text-left hover:text-primary"
          >
            {row.original.name}
          </button>
          <div className="text-xs text-muted-foreground">{row.original.description}</div>
        </div>
      ),
    },
    {
      accessorKey: "level",
      header: "Level",
      cell: ({ row }) => (
        <Badge
          variant="outline"
          className={levelBadgeClassMap[row.original.level] ?? "border-slate-300 bg-slate-100 text-slate-800"}
        >
          L{row.original.level}
        </Badge>
      ),
    },
    {
      accessorKey: "ownerName",
      header: "Committee Owner",
      cell: ({ row }) =>
        row.original.ownerName === "Owner Missing" ? (
          <div className="flex items-center gap-1 text-amber-600">
            <AlertTriangle className="h-3.5 w-3.5" />
            <span className="text-xs font-medium">{row.original.ownerName}</span>
          </div>
        ) : (
          row.original.ownerName
        ),
    },
    {
      accessorKey: "chairName",
      header: "Committee Chair",
      cell: ({ row }) =>
        row.original.chairName === "Chair Missing" ? (
          <div className="flex items-center gap-1 text-amber-600">
            <AlertTriangle className="h-3.5 w-3.5" />
            <span className="text-xs font-medium">{row.original.chairName}</span>
          </div>
        ) : (
          row.original.chairName
        ),
    },
    {
      accessorKey: "memberProgress",
      header: "Members Onboarded",
    },
    {
      accessorKey: "parentName",
      header: "Parent Committee",
    },
    {
      accessorKey: "torCommitteeId",
      header: "ToR",
      enableSorting: false,
      cell: ({ row }) =>
        row.original.torCommitteeId ? (
          <Link to={`/governance/tor/${row.original.torCommitteeId}`}>
            <Button size="sm" variant="outline">
              <Eye className="mr-1 h-3.5 w-3.5" />
              View ToR
            </Button>
          </Link>
        ) : (
          <Badge variant="outline">Not Started</Badge>
        ),
    },
    {
      id: "actions",
      header: () => <div className="text-right">Actions</div>,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {row.original.ownerName === "Owner Missing" && (
            <Button size="sm" variant="outline" onClick={() => onAssignOwner?.(row.original.id)}>
              <UserPlus className="mr-1 h-3.5 w-3.5" />
              Assign Owner
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => onEditCommittee?.(row.original.id)}>
            <Pencil className="mr-1 h-3.5 w-3.5" />
            Edit
          </Button>
          <Button size="sm" variant="outline" onClick={() => onAddSubCommittee?.(row.original.id)}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add Sub-Committee
          </Button>
        </div>
      ),
    },
  ];

  return (
    <GovernanceDataTable columns={columns} data={rows} emptyMessage="No committees added yet." />
  );
}
